<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'fixedAssets',
    path: '/fixed-assets',
    component: 'FixedAssetBrowser',
    title: () => gettext('Fixed Assets'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FixedAssetBrowser',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const selectedItems = ref([]);
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'name',
      name: $fn.value.pgettext('FixedAssetBrowser', 'Name'),
      formatter: {
        name: 'routeLink',
        to: 'fixedAsset',
        params: {
          id: 'id',
        },
      },
    },
    {
      field: 'asset_type',
      name: $fn.value.pgettext('FixedAssetBrowser', 'Type'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        formatters: [
          {
            name: 'gettext',
            context: 'FixedAssetTypes',
            conversion: 'StartCase',
            filterInput: true,
          },
        ],
      },
    },
    {
      field: 'description',
      name: $fn.value.pgettext('FixedAssetBrowser', 'Description'),
    },
    {
      field: 'inventory_no',
      name: $fn.value.pgettext('FixedAssetBrowser', 'Inventory Number'),
    },
    {
      field: 'sku',
      name: $fn.value.pgettext('FixedAssetBrowser', 'SKU'),
    },
    {
      field: 'quantity',
      name: $fn.value.pgettext('FixedAssetBrowser', 'Quantity'),
      type: 'number',
      formatter: 'number',
    },
    {
      field: 'acquisition_date',
      name: $fn.value.pgettext('FixedAssetBrowser', 'Acquisition Date'),
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('integro_api/list_fixed_asset');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.gettext('Fixed Assets'),
      icon: $config.value.fixedAsset.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_chwe4epym_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_3p4167x5r_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_4615685607825208_showModal_ek29qe30d_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'MCreateFixedAsset',
    };
  };

  const act_7315092382398562_closeModal_chwe4epym =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_chwe4epym_getActionArgs,
    });
  const inst_evh_7315092382398562_close_t68jc5rxu =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_chwe4epym,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_t68jc5rxu],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_fw01t3tok =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_byqos1kxr =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_fw01t3tok,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_byqos1kxr],
    event: 'reload',
  });
  const act_2248226175642056_setData_3p4167x5r = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_3p4167x5r_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_kv0wbzz1j =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_3p4167x5r,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_kv0wbzz1j],
      event: 'selectedRowsChanged',
    });
  const act_4615685607825208_showModal_ek29qe30d =
    new actionTypes.ShowModalAction({
      actionArgs: act_4615685607825208_showModal_ek29qe30d_getActionArgs,
    });
  const inst_evh_4615685607825208_click_0l4plfvgh =
    new actionTypes.EventHandlerImpl({
      action: act_4615685607825208_showModal_ek29qe30d,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_4615685607825208_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_4615685607825208_click_0l4plfvgh],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_t68jc5rxu,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_byqos1kxr,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_kv0wbzz1j,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_4615685607825208_click_0l4plfvgh,
    inst_evh_4615685607825208_click,
  };

  const evh_7315092382398562_close_t68jc5rxu = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_t68jc5rxu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_byqos1kxr = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_byqos1kxr.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_kv0wbzz1j = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_kv0wbzz1j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click_0l4plfvgh = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click_0l4plfvgh.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4615685607825208_click = async (event, scope) => {
    return await actions.inst_evh_4615685607825208_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="FixedAssetBrowser"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="fixedAssets"
      :headerTitle="gettext('Fixed assets')"
      :items="ds"
      checkbox-row-selection
      footer-totals
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2">
          <ItpButton
            :icon="$config.fixedAsset.createFixedAssetIcon"
            :text="pgettext('FixedAssets', 'Create fixed asset...')"
            size="sm"
            variant="light"
            @click="evh_4615685607825208_click($event, {})"
          >
          </ItpButton>
          <PageHelp path="/assets/fixed-assets.html"> </PageHelp>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
